<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { storeToRefs } from "pinia";
import SearchSelectWithKeyValue from "@/components/Reusables/Inputs/SearchSelectWithKeyValue.vue";
import { useClassificationCodes } from "@/dataProviders/ClassificationCodes";
import { codeBelongsToObjectClass, soleObjectClassCode } from "../classificationSelection";

const props = defineProps<{
  disabled?: boolean;
  bimTypeCode: string | undefined;
  ccsClassification?: string | undefined;
  classificationObjectClass?: string | undefined;
  errorMsg?: string;
  byType?: boolean;
  required: boolean;
}>();

const classificationCodes = useClassificationCodes();
const { classificationObjectClass: classificationObjectClasses } = storeToRefs(classificationCodes);

const emits = defineEmits<{
  (event: "valueChange", payload: string): void;
  (event: "onCCSClassification", payload: { CCSClassification: any; classificationObjectClass: any }): void;
}>();

const isCCType = computed(() => {
  const codes = classificationCodes.getClassificationCodes();
  if (codes.length === 0) return false;
  return codes[0].objectClass !== null;
});

const selectedClassificationCode = ref<string | undefined>(props.ccsClassification ? props.ccsClassification : props.bimTypeCode);

// The object class the material carries or the user picked. What the control SHOWS is
// `effectiveObjectClass`, which falls back to the one value a single-object-class system leaves no
// choice about.
const pickedObjectClassCode = ref<string | undefined>(props.classificationObjectClass || undefined);

const effectiveObjectClass = computed(
  () => pickedObjectClassCode.value || soleObjectClassCode(classificationObjectClasses.value)
);

/**
 * Publishes the pair the two selects are showing.
 *
 * This is the fix for RTLCA-5774. The pair used to be emitted from the code select's handler alone,
 * so the object class travelled only as a side-car of a code pick: picking the code first sent no
 * object class at all (CCI and CCS start with none — two to choose from), changing the object class
 * afterwards sent nothing, and a material that already held the code was saved unchanged even though
 * the dialog showed the object class Uniclass leaves no choice about. Each of those stores a code
 * the dashboard cannot resolve, which is the "No classification code" row in the Project Breakdown.
 *
 * Nothing is published without a code — an empty classification is not a decision, and the create
 * dialog's rows start out that way.
 */
const publishClassification = () => {
  if (!isCCType.value || !selectedClassificationCode.value) return;
  emits("onCCSClassification", {
    CCSClassification: selectedClassificationCode.value,
    classificationObjectClass: effectiveObjectClass.value,
  });
};

// The object class the control resolves by itself has no user action to hang a publish on, and the
// codes arrive asynchronously, so both `isCCType` and that resolution can settle after setup.
watch([isCCType, effectiveObjectClass], publishClassification, { immediate: true });

const handleSelectValueChange = (value: any) => {
  // Tracked for the same reason as in onCodeSelected, even though this path publishes the code
  // through its own event.
  selectedClassificationCode.value = value;
  emits("valueChange", value);
};

const onCodeSelected = (value: any) => {
  // Kept in sync so the control cannot forget the pick: SearchSelectWithKeyValue re-seeds its text
  // from `defaultValue` whenever its option list changes, and the code options are filtered by the
  // object class.
  selectedClassificationCode.value = value;
  publishClassification();
};

const onObjectClassSelected = (value: any) => {
  pickedObjectClassCode.value = value;

  // The code select filters by object class and clears when its options change, so a code that does
  // not exist under the newly chosen one is no longer on screen. Drop it here too rather than
  // publish a pair that resolves to nothing — with no code nothing is decided about the
  // classification, and `publishClassification` stays quiet.
  if (!codeBelongsToObjectClass(classificationCodes.getClassificationCodes(), selectedClassificationCode.value, value)) {
    selectedClassificationCode.value = undefined;
  }

  publishClassification();
};

const classificationOptions = computed(() => {
  let codes = classificationCodes.getClassificationCodes();

  if (isCCType.value && effectiveObjectClass.value) {
    codes = codes.filter((code) => code.objectClass?.code === effectiveObjectClass.value);
  }

  return codes.map((code) => ({
    name: `${getSpacesFromCodeLength(code.code)}${code.code} - ${classificationCodes.resolveLabel(code)}`,
    value: code.code,
  }));
});

const objectClassOptions = computed(() => {
  if (isCCType.value) {
    return classificationObjectClasses.value.map((obj) => ({
      name: `${obj.code} - ${obj.name}`,
      value: obj.code,
    }));
  }

  const selected = classificationCodes.getClassificationCodes().find((code) => code.code === selectedClassificationCode.value);
  const obj = selected?.objectClass;
  return obj ? [{ name: `${obj.code} - ${obj.name}`, value: obj.code }] : [];
});

const getSpacesFromCodeLength = (code: string) => {
  return Array.from({ length: code.length }, () => "\u205F").join("");
};
</script>

<template>
  <div class="flex w-full flex-col gap-y-2">
    <SearchSelectWithKeyValue
      v-if="isCCType"
      required
      :label="props.byType ? 'Classification code' : ''"
      :options="objectClassOptions"
      :default-value="effectiveObjectClass"
      :disabled="disabled"
      :error-msg="errorMsg"
      placeholder="Select"
      @option-selected="onObjectClassSelected" />

    <SearchSelectWithKeyValue
      :required="props.required"
      :options="classificationOptions"
      :default-value="selectedClassificationCode"
      :disabled="disabled"
      :error-msg="errorMsg"
      placeholder="Select"
      @option-selected="(val: string | number) => (isCCType ? onCodeSelected(val) : handleSelectValueChange(val))" />
  </div>
</template>
